import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { animationConfig } from '../../lib/animations';

// Higher-order component for smooth loading transitions
export const withLoading = (WrappedComponent, SkeletonComponent, options = {}) => {
  const {
    transitionDuration = 'normal',
    transitionEasing = 'material',
    skeletonProps = {},
    loadingDelay = 0, // Minimum time to show loading state
    fadeInDelay = 0.1 // Delay before content fades in
  } = options;

  return function WithLoadingComponent({ loading, children, ...props }) {
    const [showContent, setShowContent] = React.useState(false);
    const [minLoadingTime, setMinLoadingTime] = React.useState(true);

    React.useEffect(() => {
      if (!loading) {
        // Ensure minimum loading time for smooth UX
        const timer = setTimeout(() => {
          setMinLoadingTime(false);
        }, loadingDelay);

        return () => clearTimeout(timer);
      } else {
        setMinLoadingTime(true);
        setShowContent(false);
      }
    }, [loading, loadingDelay]);

    React.useEffect(() => {
      if (!minLoadingTime && !loading) {
        // Small delay before showing content for smooth transition
        const timer = setTimeout(() => {
          setShowContent(true);
        }, fadeInDelay);

        return () => clearTimeout(timer);
      }
    }, [minLoadingTime, loading, fadeInDelay]);

    const isActuallyLoading = loading || minLoadingTime;

    return (
      <div className="relative">
        <AnimatePresence mode="wait">
          {isActuallyLoading ? (
            <motion.div
              key="skeleton"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: animationConfig.duration.fast,
                ease: animationConfig.easing.material
              }}
            >
              <SkeletonComponent {...skeletonProps} />
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: animationConfig.duration[transitionDuration],
                ease: animationConfig.easing[transitionEasing],
                delay: fadeInDelay
              }}
            >
              <WrappedComponent {...props}>
                {children}
              </WrappedComponent>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };
};

// Hook for managing loading states with smooth transitions
export const useLoadingTransition = (loading, options = {}) => {
  const {
    minLoadingTime = 300,
    fadeInDelay = 100
  } = options;

  const [showContent, setShowContent] = React.useState(false);
  const [internalLoading, setInternalLoading] = React.useState(true);

  React.useEffect(() => {
    if (!loading) {
      // Ensure minimum loading time
      const timer = setTimeout(() => {
        setInternalLoading(false);
      }, minLoadingTime);

      return () => clearTimeout(timer);
    } else {
      setInternalLoading(true);
      setShowContent(false);
    }
  }, [loading, minLoadingTime]);

  React.useEffect(() => {
    if (!internalLoading) {
      // Small delay before showing content
      const timer = setTimeout(() => {
        setShowContent(true);
      }, fadeInDelay);

      return () => clearTimeout(timer);
    }
  }, [internalLoading, fadeInDelay]);

  return {
    isLoading: internalLoading,
    showContent,
    shouldShowSkeleton: internalLoading
  };
};

// Component wrapper for conditional rendering with loading states
export const LoadingWrapper = ({ 
  loading, 
  skeleton, 
  children, 
  className = '',
  transitionDuration = 'normal',
  transitionEasing = 'material',
  ...props 
}) => {
  const { shouldShowSkeleton } = useLoadingTransition(loading, {
    minLoadingTime: 300,
    fadeInDelay: 100
  });

  return (
    <div className={className} {...props}>
      <AnimatePresence mode="wait">
        {shouldShowSkeleton ? (
          <motion.div
            key="skeleton"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: animationConfig.duration.fast,
              ease: animationConfig.easing.material
            }}
          >
            {skeleton}
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: animationConfig.duration[transitionDuration],
              ease: animationConfig.easing[transitionEasing]
            }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Staggered loading wrapper for lists
export const StaggeredLoadingWrapper = ({ 
  loading, 
  skeleton, 
  children, 
  staggerDelay = 0.1,
  className = '',
  ...props 
}) => {
  const { shouldShowSkeleton } = useLoadingTransition(loading, {
    minLoadingTime: 400,
    fadeInDelay: 200
  });

  return (
    <div className={className} {...props}>
      <AnimatePresence mode="wait">
        {shouldShowSkeleton ? (
          <motion.div
            key="skeleton"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: animationConfig.duration.fast,
              ease: animationConfig.easing.material
            }}
          >
            {skeleton}
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial="initial"
            animate="animate"
            variants={{
              initial: { opacity: 0 },
              animate: {
                opacity: 1,
                transition: {
                  staggerChildren: staggerDelay,
                  delayChildren: 0.1
                }
              }
            }}
            transition={{
              duration: animationConfig.duration.normal,
              ease: animationConfig.easing.material
            }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Page-level loading wrapper
export const PageLoadingWrapper = ({ 
  loading, 
  skeleton, 
  children, 
  className = '',
  ...props 
}) => {
  const { shouldShowSkeleton } = useLoadingTransition(loading, {
    minLoadingTime: 500,
    fadeInDelay: 200
  });

  return (
    <div className={className} {...props}>
      <AnimatePresence mode="wait">
        {shouldShowSkeleton ? (
          <motion.div
            key="skeleton"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{
              duration: animationConfig.duration.normal,
              ease: animationConfig.easing.material
            }}
          >
            {skeleton}
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: animationConfig.duration.slow,
              ease: animationConfig.easing.smooth
            }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Export all loading utilities
export default {
  withLoading,
  useLoadingTransition,
  LoadingWrapper,
  StaggeredLoadingWrapper,
  PageLoadingWrapper
};
